/**
 * @fileoverview Board - ゲーム盤面データ管理クラス
 */

import { ROWS, COLS } from '../core/constants.js';

/**
 * Board - ゲーム盤面の内部データ管理クラス（簡素化版）
 *
 * 責務:
 * - 盤面データの保持と管理（2次元配列）
 * - 基本的な盤面操作（取得、設定、削除、移動、交換）
 * - 座標系の正規化とバリデーション
 * - 基本的な検索・イテレーション機能
 */
class Board {
  /**
   * コンストラクタ
   * @param {number} rows - 行数（デフォルト: ROWS定数）
   * @param {number} cols - 列数（デフォルト: COLS定数）
   */
  constructor(rows = ROWS, cols = COLS) {
    this.rows = rows;
    this.cols = cols;
    this.pieces = this.initializeGrid();
    this._nextPieceId = 1; // シンプルなID採番
  }

  // 初期化

  /**
   * 盤面グリッドの初期化
   * @returns {Array<Array<Object|null>>} 初期化された2次元配列
   */
  initializeGrid() {
    // より関数型的で読みやすい実装
    return Array.from({ length: this.rows }, () => Array.from({ length: this.cols }, () => null));
  }

  /**
   * 盤面を完全にクリア
   */
  clear() {
    this.pieces = this.initializeGrid();
  }

  // 座標系とバリデーション

  /**
   * 座標の有効性をチェック
   * @param {number} row - 行インデックス
   * @param {number} col - 列インデックス
   * @returns {boolean} 座標が有効かどうか
   */
  isValidPosition(row, col) {
    return (
      Number.isInteger(row) &&
      Number.isInteger(col) &&
      row >= 0 &&
      row < this.rows &&
      col >= 0 &&
      col < this.cols
    );
  }

  /**
   * 座標のバリデーション（内部使用）
   * @private
   * @param {number} row - 行インデックス
   * @param {number} col - 列インデックス
   * @returns {{row: number, col: number}} 座標オブジェクト
   * @throws {Error} 無効な座標の場合
   */
  _validatePosition(row, col) {
    if (!this.isValidPosition(row, col)) {
      throw new Error(
        `Invalid position: (${row}, ${col}). Valid range: 0-${this.rows - 1}, 0-${this.cols - 1}`,
      );
    }
    return { row, col };
  }

  // 基本的なピース操作

  /**
   * 指定位置のピースを取得
   * @param {number} row - 行インデックス
   * @param {number} col - 列インデックス
   * @returns {Object|null} ピースオブジェクトまたはnull
   * @throws {Error} 無効な座標の場合
   */
  getPiece(row, col) {
    const pos = this._validatePosition(row, col);
    return this.pieces[pos.row][pos.col];
  }

  /**
   * 指定位置にピースを設定
   * @param {number} row - 行インデックス
   * @param {number} col - 列インデックス
   * @param {Object|null} piece - ピースオブジェクトまたはnull
   * @returns {Object|null} 以前のピース
   * @throws {Error} 無効な座標の場合
   */
  setPiece(row, col, piece) {
    const pos = this._validatePosition(row, col);
    const previousPiece = this.pieces[pos.row][pos.col];

    // ピースの基本情報を設定
    if (piece) {
      // ID管理を共通化
      this._ensurePieceId(piece);
      piece.position = [pos.row, pos.col];
    }

    this.pieces[pos.row][pos.col] = piece;
    return previousPiece;
  }

  /**
   * 指定位置のピースを削除
   * @param {number} row - 行インデックス
   * @param {number} col - 列インデックス
   * @returns {Object|null} 削除されたピース
   * @throws {Error} 無効な座標の場合
   */
  removePiece(row, col) {
    return this.setPiece(row, col, null);
  }

  /**
   * ピースを1マスから別のマスへ移動
   * @param {number} fromRow
   * @param {number} fromCol
   * @param {number} toRow
   * @param {number} toCol
   * @returns {{movedPiece: Object|null, from: {row:number,col:number}, to: {row:number,col:number}, replacedPiece: Object|null}}
   */
  movePiece(fromRow, fromCol, toRow, toCol) {
    const from = this._validatePosition(fromRow, fromCol);
    const to = this._validatePosition(toRow, toCol);

    const moving = this.pieces[from.row][from.col];
    const replaced = this.pieces[to.row][to.col];

    // 目的地に移動
    if (moving) {
      moving.position = [to.row, to.col];
    }
    this.pieces[to.row][to.col] = moving;

    // 移動元を空に
    this.pieces[from.row][from.col] = null;

    return { movedPiece: moving, from, to, replacedPiece: replaced };
  }

  /**
   * 指定位置が空かどうかをチェック
   * @param {number} row - 行インデックス
   * @param {number} col - 列インデックス
   * @returns {boolean} 空の場合true
   * @throws {Error} 無効な座標の場合
   */
  isEmpty(row, col) {
    return this.getPiece(row, col) === null;
  }

  // 高度な操作

  /**
   * 2つのピースを交換
   * @param {number} row1 - 1つ目のピースの行
   * @param {number} col1 - 1つ目のピースの列
   * @param {number} row2 - 2つ目のピースの行
   * @param {number} col2 - 2つ目のピースの列
   * @returns {{piece1: Object|null, piece2: Object|null}} 交換されたピース
   * @throws {Error} 無効な座標の場合
   */
  swapPieces(row1, col1, row2, col2) {
    const pos1 = this._validatePosition(row1, col1);
    const pos2 = this._validatePosition(row2, col2);

    const piece1 = this.pieces[pos1.row][pos1.col];
    const piece2 = this.pieces[pos2.row][pos2.col];

    // 位置情報を更新
    if (piece1) piece1.position = [pos2.row, pos2.col];
    if (piece2) piece2.position = [pos1.row, pos1.col];

    // 交換実行
    this.pieces[pos1.row][pos1.col] = piece2;
    this.pieces[pos2.row][pos2.col] = piece1;

    return { piece1, piece2 };
  }

  /**
   * 盤面全体をシャッフル（既存ピースの位置だけランダム入れ替え）
   * @returns {Array<{from: {row: number, col: number}, to: {row: number, col: number}, piece: Object}>}
   *          各ピースの移動情報の配列
   */
  shuffleBoard() {
    // 全ピースを収集（nullでないもののみ）
    const allPieces = [];
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const piece = this.pieces[row][col];
        if (piece) {
          allPieces.push({
            piece,
            originalPosition: { row, col },
          });
        }
      }
    }

    // Fisher-Yates シャッフル
    for (let i = allPieces.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allPieces[i], allPieces[j]] = [allPieces[j], allPieces[i]];
    }

    // 新しい位置に配置 & 移動情報を記録
    const transitions = [];
    for (let i = 0; i < allPieces.length; i++) {
      const { piece, originalPosition } = allPieces[i];
      const newPosition = allPieces[(i + 1) % allPieces.length].originalPosition;

      // 盤面に配置
      piece.position = [newPosition.row, newPosition.col];
      this.pieces[newPosition.row][newPosition.col] = piece;

      // アニメーション用の移動情報を記録
      if (originalPosition.row !== newPosition.row || originalPosition.col !== newPosition.col) {
        transitions.push({
          from: originalPosition,
          to: newPosition,
          piece,
        });
      }
    }

    return transitions;
  }

  /**
   * ピースIDの確保（共通化）
   * @private
   */
  _ensurePieceId(piece) {
    if (piece.id === undefined || piece.id === null) {
      piece.id = this._nextPieceId++;
    }
  }

  // 検索とイテレーション

  /**
   * 全ピースに対してコールバック関数を実行
   * @param {Function} callback - (piece, row, col) => void
   */
  forEachPiece(callback) {
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const piece = this.pieces[row][col];
        callback(piece, row, col);
      }
    }
  }

  /**
   * 条件に一致するピースを検索
   * @param {Function} predicate - (piece, row, col) => boolean
   * @returns {Array<{piece: Object, row: number, col: number}>} 一致するピースのリスト
   */
  findPieces(predicate) {
    const results = [];
    this.forEachPiece((piece, row, col) => {
      if (predicate(piece, row, col)) {
        results.push({ piece, row, col });
      }
    });
    return results;
  }

  /**
   * IDでピースを検索
   * @param {number} pieceId - ピースID
   * @returns {Object|null} {piece, row, col} または null
   */
  getById(pieceId) {
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const piece = this.pieces[row][col];
        if (piece?.id === pieceId) {
          return { piece, row, col };
        }
      }
    }
    return null;
  }

  /**
   * 特殊ピースを全て取得
   * @param {string|Array<string>} specialTypes - 特殊タイプ（省略時は全て）
   * @returns {Array<{piece: Object, row: number, col: number}>} 特殊ピースのリスト
   */
  getSpecialPieces(specialTypes = null) {
    return this.findPieces((piece) => {
      if (!piece || !piece.specialType) return false;
      if (!specialTypes) return true;
      const types = Array.isArray(specialTypes) ? specialTypes : [specialTypes];
      return types.includes(piece.specialType);
    });
  }
}

export default Board;
